/**
 * How an employee's pay is split across funds and programs (migration 0083;
 * N5 of the #55 plan, §B.16.6). The rules are the API's
 * (apps/api/src/services/employee-allocations.ts); this shows and edits them.
 *
 * Shown only to a business that keeps funds or has a project: with neither,
 * there is nothing to split across. Shares are percents to the hundredth,
 * sent as basis points, and come to 100% or there are none, which is all to
 * the defaults. Save is pressable whatever the rows say, and answers with a
 * sentence when the sum is wrong, rather than doing nothing (CLAUDE.md).
 *
 * A fund or program archived since is marked here, as Rules marks one (review
 * PY-4): the next run for this employee is refused until it is changed.
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type AllocationShare, type Fund, type Project } from '../api.js';
import { projectWord, useBusiness, useUsesFunds } from '../auth/AuthProvider.js';
import { InfoNote } from './InfoNote.js';

interface Row {
  key: number;
  fundId: string;
  projectId: string;
  percent: string;
}

/** "40", "33.33": a percent to the hundredth, as basis points; null if it is not one. */
export function toBasisPoints(percent: string): number | null {
  const text = percent.trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

function percentOf(shareBp: number): string {
  return (shareBp / 100).toFixed(2).replace(/\.00$/, '');
}

export function EmployeeAllocation({
  employeeId,
  employeeName,
  allocation,
  mayChange,
  onSaved,
}: {
  employeeId: string;
  employeeName: string;
  allocation: AllocationShare[];
  mayChange: boolean;
  onSaved: (allocation: AllocationShare[]) => void;
}) {
  const usesFunds = useUsesFunds();
  const word = projectWord(useBusiness());
  const [funds, setFunds] = useState<Fund[]>([]);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nextKey, setNextKey] = useState(1);

  useEffect(() => {
    let cancelled = false;
    Promise.all([usesFunds ? api.listFunds() : Promise.resolve({ usesFunds: false, funds: [] as Fund[] }), api.listProjects()])
      .then(([fundList, projectList]) => {
        if (cancelled) return;
        setFunds(fundList.funds);
        setProjects(projectList.projects);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [usesFunds]);

  // Nothing to split across: no funds and no project.
  if (projects === null || (!usesFunds && projects.length === 0 && allocation.length === 0)) return null;

  const defaultFund = funds.find((fund) => fund.isDefault);
  const defaultProgram = projects.find((project) => project.isDefault);
  const fundLabel = (share: AllocationShare) =>
    share.fundId === null ? (defaultFund ? `${defaultFund.code} (Default)` : 'Default') : `${share.fundCode}${share.fundArchived ? ' (Archived)' : ''}`;
  const projectLabel = (share: AllocationShare) =>
    share.projectId === null
      ? defaultProgram
        ? `${defaultProgram.name} (Default)`
        : 'None'
      : `${share.projectName}${share.projectArchived ? ' (Archived)' : ''}`;

  function startEditing() {
    const start = allocation.length > 0 ? allocation : [{ fundId: null, projectId: null, shareBp: 10_000 } as AllocationShare];
    setRows(start.map((share, index) => ({ key: index, fundId: share.fundId ?? '', projectId: share.projectId ?? '', percent: percentOf(share.shareBp) })));
    setNextKey(start.length);
    setError(null);
  }

  const parsed = (rows ?? []).map((row) => toBasisPoints(row.percent));
  const totalBp = parsed.reduce<number>((sum, bp) => sum + (bp ?? 0), 0);

  async function save(shares: Array<{ fundId: string | null; projectId: string | null; shareBp: number }>) {
    setBusy(true);
    try {
      const result = await api.setEmployeeAllocation(employeeId, shares);
      onSaved(result.allocation);
      setRows(null);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save the allocation.');
    } finally {
      setBusy(false);
    }
  }

  function submit() {
    if (rows === null) return;
    if (parsed.some((bp) => bp === null || bp === 0)) {
      setError('Each share is a percent more than 0, to the hundredth, such as 40 or 33.33.');
      return;
    }
    if (totalBp !== 10_000) {
      setError(`The shares come to ${percentOf(totalBp)}%. They come to 100%, or choose Clear to use the defaults.`);
      return;
    }
    void save(rows.map((row, index) => ({ fundId: row.fundId || null, projectId: row.projectId || null, shareBp: parsed[index]! })));
  }

  const update = (key: number, change: Partial<Row>) =>
    setRows((current) => (current ?? []).map((row) => (row.key === key ? { ...row, ...change } : row)));

  return (
    <section className="settings-section" aria-labelledby="employee-allocation">
      <h2 id="employee-allocation">Allocation</h2>
      <p className="muted">
        How {employeeName}&apos;s wages and the employer&apos;s taxes on them are split across{' '}
        {usesFunds ? `funds and ${word.toLowerCase()}s` : `${word.toLowerCase()}s`}.{' '}
        <InfoNote label="What The Allocation Does">
          A pay run copies the allocation into its lines, so changing it moves no run already made. Only the wages and the
          employer&apos;s taxes are split; what is withheld and owed, and the cash, stay in the default fund, so a fund&apos;s share
          is owed to General until it is paid back. A grant&apos;s reimbursement is entered the same way: the cash in General and
          the revenue in the grant&apos;s fund, which settles what the fund owed. With no allocation, everything goes to the
          defaults.
        </InfoNote>
      </p>

      {rows === null ? (
        <>
          {allocation.length === 0 ? (
            <p className="muted">None recorded, so every run goes to the defaults.</p>
          ) : (
            <div className="table-scroll">
              <table className="lines" aria-label="Allocation">
                <thead>
                  <tr>
                    {usesFunds && <th>Fund</th>}
                    <th>{word}</th>
                    <th className="amount">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {allocation.map((share) => (
                    <tr key={`${share.fundId ?? ''}|${share.projectId ?? ''}`}>
                      {usesFunds && <td>{fundLabel(share)}</td>}
                      <td>{projectLabel(share)}</td>
                      <td className="amount">{percentOf(share.shareBp)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {mayChange && (
            <button type="button" className="link" onClick={startEditing}>
              Change Allocation
            </button>
          )}
        </>
      ) : (
        <div className="new-account employee-form" role="group" aria-label="Change Allocation">
          <div className="employee-fields">
          {rows.map((row, index) => (
            <div className="field-row" key={row.key}>
              {usesFunds && (
                <label className="field">
                  <span>Fund</span>
                  <select aria-label={`Fund for share ${index + 1}`} value={row.fundId} onChange={(event) => update(row.key, { fundId: event.target.value })}>
                    <option value="">{defaultFund ? `${defaultFund.code} (Default)` : 'Default'}</option>
                    {/* A share's own fund stays an option once archived, marked, so the select never changes it unseen. */}
                    {funds
                      .filter((fund) => !fund.isDefault && (fund.archivedAt === null || fund.id === row.fundId))
                      .map((fund) => (
                        <option key={fund.id} value={fund.id}>
                          {fund.code} {fund.name}
                          {fund.archivedAt !== null ? ' (Archived)' : ''}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              <label className="field">
                <span>{word}</span>
                <select aria-label={`${word} for share ${index + 1}`} value={row.projectId} onChange={(event) => update(row.key, { projectId: event.target.value })}>
                  <option value="">{defaultProgram ? `${defaultProgram.name} (Default)` : 'None'}</option>
                  {projects
                    .filter((project) => !project.isDefault && (project.archivedAt === null || project.id === row.projectId))
                    .map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                        {project.archivedAt !== null ? ' (Archived)' : ''}
                      </option>
                    ))}
                </select>
              </label>
              <label className="field field-medium">
                <span>Percent</span>
                <input
                  aria-label={`Percent for share ${index + 1}`}
                  inputMode="decimal"
                  value={row.percent}
                  onChange={(event) => {
                    const percent = event.target.value;
                    update(row.key, { percent });
                  }}
                />
              </label>
              {rows.length > 1 && (
                <button type="button" className="link" onClick={() => setRows(rows.filter((other) => other.key !== row.key))}>
                  Remove Share {index + 1}
                </button>
              )}
            </div>
          ))}
          </div>
          <p className="muted" aria-live="polite">
            Total {percentOf(totalBp)}%
          </p>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <div className="row">
            <button
              type="button"
              className="link"
              onClick={() => {
                setRows([...rows, { key: nextKey, fundId: '', projectId: '', percent: '' }]);
                setNextKey(nextKey + 1);
              }}
            >
              Add a Share
            </button>
            <button type="button" disabled={busy} onClick={submit}>
              Save Allocation
            </button>
            <button type="button" disabled={busy} onClick={() => void save([])}>
              Clear
            </button>
            <button type="button" className="link" onClick={() => setRows(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
